import { forwardRef } from 'react';
import 'swiper/swiper-bundle.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import type { Call } from '../types.ts';
import { landingYoutube, popupToast } from '@/api/nativeBridge.ts';
import { logger } from '@/utils/logger.ts';
import { useWindowInnerHeightResize } from '@/hooks/useWindowResize.ts';

interface RotateSwiperProps {
  callList: Call[];
}

const RotateSwiperSection = forwardRef<HTMLDivElement, RotateSwiperProps>(
  ({ callList }, ref) => {
    const slidesData =
      callList?.[0]?.searchResult?.map((result) => ({
        imgSrc: result.youtube.contents?.[0]?.isShort
          ? result.youtube.contents?.[0]?.thumbnailUrl
          : '/res/img/rotate_card_empty.webp?v=1',
        imgAlt: result.youtube.contents?.[0]?.isShort
          ? '유튜브 썸네일'
          : 'empty',
        title: result.youtube.query, // '# 타이틀 두줄, 영역 넘치게 된다면 넘치는 만큼 말줄임(...) 처리됩니다.',
        desc: result.youtube.tags || result.youtube.contents || [],
        videoId: result.youtube.contents?.[0]?.videoId,
      })) || [];

    const webviewHeight = useWindowInnerHeightResize();
    const minimumClientHeight = 640;
    const needAdjustPosterSize = webviewHeight < minimumClientHeight;
    const defaultImageHeight = 348;
    const defaultImageWidth = 232;

    const clickYoutube = (videoId: string, index: number) => {
      if (videoId) {
        landingYoutube(videoId, (index + 1).toString());
      } else {
        popupToast(1, '유튜브 쇼츠가 존재하지 않습니다.');
      }
    };

    // 바텀 시트를 포함 하기에 작은 크기면
    function swiperAdjustSizeStyle() {
      if (needAdjustPosterSize) {
        logger.debug('ADJSUT SWIPER ITEM POSTER SIZE :: ', window.innerHeight);
        const notEnoughHeight = minimumClientHeight - webviewHeight;
        const targetSize = defaultImageHeight - notEnoughHeight; //목표 사이즈
        const ratio = targetSize / defaultImageHeight;
        return {
          width: defaultImageWidth * ratio,
          height: defaultImageHeight * ratio,
        };
      } else {
        return {};
      }
    }

    // 슬라이드 구조를 함수나 별도 컴포넌트로 추출할 수도 있음
    const renderSlide = (
      data: {
        imgSrc: string;
        imgAlt: string;
        title: string;
        desc: string[];
        videoId: string;
      },
      index: number,
    ) => {
      return (
        <SwiperSlide key={index} style={swiperAdjustSizeStyle()}>
          <div
            className="card-item"
            onClick={() => {
              clickYoutube(data.videoId, index);
            }}
          >
            <img
              src={data.imgSrc
                .replace('/vi/', '/vi_webp/')
                .replace('.jpg', '.webp')}
              alt={data.imgAlt}
              className="card-img"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = data.imgSrc;
              }}
            />
            <div className="card-title">
              <p className="title">{data.title}</p>
              <div className="desc">
                {data.desc.map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
            </div>
          </div>
        </SwiperSlide>
      );
    };

    return (
      <div className="swiper rotateSwiper" ref={ref}>
        <Swiper
          slidesPerView={'auto'}
          centeredSlides
          spaceBetween={90}
          grabCursor
          pagination={{
            clickable: true,
          }}
          modules={[Pagination]}
        >
          {slidesData.map(renderSlide)}
        </Swiper>
      </div>
    );
  },
);

export default RotateSwiperSection;
